import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, inject, OnDestroy, PLATFORM_ID, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';

import {
  HEADER_NAV_HIGHLIGHT_DELAY_MS,
  HEADER_SCROLL_THRESHOLD_PX,
  MENU_CLOSE_ANIMATION_MS,
  MENU_OPEN_ANIMATION_MS,
} from '../../shared/constants/app-config.const';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: false,
})
export class HeaderComponent implements AfterViewInit, OnDestroy {
  readonly navigation = viewChild.required<ElementRef>('navigation');
  isScrolled: boolean = false;
  showMenu: boolean = false;
  openMenu: boolean = false;
  hashKey: string = '';
  currentLanguage = '';

  /** Update scroll state — uses DOCUMENT defaultView to avoid direct window access */
  @HostListener('window:scroll')
  checkScroll() {
    this.isScrolled = (this.document.defaultView?.scrollY ?? 0) >= HEADER_SCROLL_THRESHOLD_PX;
  }

  navigations = [
    { routeName: '', navName: 'top_navigation.nav_home', isPage: false },
    { routeName: 'our-journey', navName: 'top_navigation.nav_history', isPage: false },
    { routeName: 'about-us', navName: 'top_navigation.nav_about', isPage: false },
    // TODO: no dedicated Event page in the Figma design yet — links home until one is designed
    { routeName: '', navName: 'top_navigation.nav_event', isPage: false },
    { routeName: 'gallery', navName: 'top_navigation.nav_gallery', isPage: true },
    { routeName: 'our-programs', navName: 'top_navigation.nav_programs', isPage: false },
    { routeName: 'get-in-touch', navName: 'top_navigation.nav_contact', isPage: false },
    // TODO: no dedicated Our Team page in the Figma design yet — links home until one is designed
    { routeName: '', navName: 'top_navigation.nav_team', isPage: false },
  ];

  private readonly _platformId = inject(PLATFORM_ID);
  // Use the DOCUMENT token rather than the global `document` for SSR safety
  private readonly document = inject(DOCUMENT);
  private readonly translate = inject(TranslateService);

  constructor() {
    this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe((params: LangChangeEvent) => {
      this.currentLanguage = params.lang;
      if (isPlatformBrowser(this._platformId)) localStorage.setItem('lang', this.currentLanguage);
    });
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this._platformId)) {
      if (!this.currentLanguage) {
        this.currentLanguage = localStorage.getItem('lang') || 'en';
      }

      // Use DOCUMENT to access hash — avoids direct window.location reference
      const loc = this.document.location;
      this.hashKey = loc?.hash ? loc.hash.substring(1) : '';
      // Delay allows the view to fully render before highlighting the active nav link
      setTimeout(() => this.addActive(this.navigation().nativeElement, this.hashKey, true), HEADER_NAV_HIGHLIGHT_DELAY_MS);
    }
  }

  addActive(event: Event, section: string, autoMode: boolean = false) {
    if (!autoMode) this.hashKey = section;
    const target: HTMLElement = event.currentTarget ? (event.currentTarget as HTMLElement) : (event.target as HTMLElement);
    let linkList = target.parentNode?.querySelectorAll('a') || [];
    linkList.forEach((link: HTMLAnchorElement) => {
      if (link === event.target || link.title === this.hashKey) {
        link.classList.add('active');
      } else {
        link.classList.remove('active');
      }
    });
  }

  openCloseMenu(): void {
    if (this.showMenu) {
      this.openMenu = !this.openMenu;
      // Remove body scroll lock applied when menu opened
      this.document.body.classList.remove('modal-open');
      // Wait for CSS close animation before hiding the menu element
      setTimeout(() => (this.showMenu = !this.showMenu), MENU_CLOSE_ANIMATION_MS);
    } else {
      this.showMenu = !this.showMenu;
      // Lock body scroll while menu is visible
      this.document.body.classList.add('modal-open');
      // Wait for CSS open animation before showing the overlay
      setTimeout(() => (this.openMenu = !this.openMenu), MENU_OPEN_ANIMATION_MS);
    }
  }

  ngOnDestroy(): void {}
}
